import { TEST_ID_INVITATION } from "e2e/testId";
import { SendIcon, TrashIcon } from "lucide-react-native";
import React, { memo } from "react";

import { Button } from "@/components/atoms/Button";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import { InvitationsInvitationFragment } from "@/generated/graphql";
import { ifWeb } from "@/utils/platform";

/**
 * Props for the InvitationItemView component.
 */
interface Props {
  item: InvitationsInvitationFragment;
  nameDisplay: string;
  senderNameDisplay: string;
  loading: boolean;
  onDelete: () => void;
  onResend: () => void;
}

/**
 * View component for displaying a single sent invitation item with action buttons.
 * @param root0 - The component props
 * @param root0.item - The invitation data fragment containing invitation details and status
 * @param root0.nameDisplay - The formatted display name of the invitation receiver
 * @param root0.senderNameDisplay - The formatted display name of the invitation sender
 * @param root0.loading - Boolean indicating if resend or delete operation is in progress
 * @param root0.onDelete - Callback function to handle invitation deletion
 * @param root0.onResend - Callback function to handle resending the invitation
 */
const InvitationItemView = ({
  item,
  nameDisplay,
  senderNameDisplay,
  loading,
  onDelete,
  onResend,
}: Props) => (
  <Stack className="p-3">
    <Stack
      direction="horizontal"
      className="w-full items-center justify-between gap-3"
    >
      <Stack className="gap-2">
        <Text
          variant="unstyled"
          className={`font-heading ${ifWeb("text-title-lg", "text-display-sm")}`}
        >
          {nameDisplay}
        </Text>
        {/* SE-6773: the web arm used to name an `md` size step, for which this
            theme has no fontSize token — so it compiled to nothing and BOTH
            arms already rendered the Text atom's own default (size "md" ->
            text-title-sm); the native arm carries no size class at all. Naming
            that default keeps the render identical. */}
        <Text
          variant="unstyled"
          className={`text-content-muted ${ifWeb("text-title-sm", "")}`}
        >
          {item.role?.name} from {senderNameDisplay}
        </Text>
      </Stack>
      {item.status === "PENDING" && (
        <Stack direction="horizontal" className="items-center gap-4">
          <Button
            variant="link"
            accessibilityLabel="Resend invitation"
            onPress={onResend}
            isDisabled={loading}
            icon={SendIcon}
          />
          <Button
            testID={TEST_ID_INVITATION.DELETE_ITEM_BUTTON}
            variant="link"
            accessibilityLabel="Delete invitation"
            onPress={onDelete}
            isDisabled={loading}
            icon={TrashIcon}
          />
        </Stack>
      )}
    </Stack>
  </Stack>
);

InvitationItemView.displayName = "InvitationItemView";

export default memo(InvitationItemView);
